import React, { useState, useEffect, useMemo, forwardRef } from 'react';
import { Offcanvas, Button, Form, Row, Col, Dropdown } from 'react-bootstrap';
import { BiPencil } from "react-icons/bi";
import { BsX, BsPencil, BsCheck } from "react-icons/bs";
import {
    FaCogs,
    FaIdCard,
    FaNetworkWired,
    FaServer,
    FaBuilding
} from "react-icons/fa";
import TenantInfoDetails from './TenantInfoDetails';
import axios from 'axios';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import ReactPaginate from 'react-paginate';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
    const userInfo = JSON.parse(localStorage.getItem('user-info') || '{}');
    const token = userInfo?.token;
    const origin = localStorage.getItem('user-origin');
    return {
        Authorization: `Bearer ${token}`,
        'Tenant-Origin': origin
    };
};

const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
    }
});

const CustomToggle = forwardRef(({ children, onClick }, ref) => (
    <button
        ref={ref}
        type="button"
        onClick={(e) => {
            e.preventDefault();
            onClick(e);
        }}
        className="btn rounded border d-flex align-items-center justify-content-center shadow-sm bg-white text-muted px-3"
        style={{ height: '38px' }}
        title="Filter by Expiry"
    >
        <i className="mdi mdi-tune font-size-18 me-2"></i> Filter
    </button>
));

const parseValidDate = (dateStr) => {
    if (!dateStr) return null;
    const datePart = String(dateStr).split('T')[0].trim();
    const parts = datePart.split('-');
    if (parts.length === 3) {
        if (parts[0].length === 4) {
            const year = Number(parts[0]);
            const month = Number(parts[1]) - 1;
            const day = Number(parts[2]);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
        } else if (parts[2].length === 4) {
            const day = Number(parts[0]);
            const month = Number(parts[1]) - 1;
            const year = Number(parts[2]);
            const d = new Date(year, month, day);
            return isNaN(d.getTime()) ? null : d;
        }
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
};

const formatDate = (date) => {
    if (!date) return '-';
    const d = date instanceof Date ? date : parseValidDate(date);
    if (!d || isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

const getLicenseStatus = (info) => {
    const validityDateStr = info.extended_validity || info.validity;
    if (!validityDateStr) return 'Active';
    const validityDate = parseValidDate(validityDateStr);
    if (!validityDate) return 'Active';
    validityDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (validityDate < today) return 'Expired';
    if (info.extended_validity) return 'Extended';
    return 'Active';
};

const getDaysRemaining = (validityStr) => {
    if (!validityStr) return '-';
    const validityDate = parseValidDate(validityStr);
    if (!validityDate) return '-';
    validityDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const diffTime = validityDate - today;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Expires Today';
    return `${diffDays} Days`;
};

const SetupInfoDetails = ({ show, onHide, setupData, onEditSetup, activeExpiryFilter, defaultTab = 'licenseList', autoOpenLicenseId, onSetupUpdated }) => {
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [isEditingKey, setIsEditingKey] = useState(false);
    const [editedKey, setEditedKey] = useState("");

    const [isEditingClientName, setIsEditingClientName] = useState(false);
    const [editedClientName, setEditedClientName] = useState("");

    const [isEditingClientId, setIsEditingClientId] = useState(false);
    const [editedClientId, setEditedClientId] = useState("");

    // Add local state to reflect the update immediately without a full refresh
    const [localSetupData, setLocalSetupData] = useState(setupData);

    // Licenses State
    const [licenses, setLicenses] = useState([]);
    const [isLoadingLicenses, setIsLoadingLicenses] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expiryFilter, setExpiryFilter] = useState(activeExpiryFilter || '');
    const [accStatusTab, setAccStatusTab] = useState('all'); // 'all' | 'active' | 'deactivated' | 'suspended'
    const [currentPage, setCurrentPage] = useState(0);

    // Tenant Details Offcanvas State
    const [showTenantDetails, setShowTenantDetails] = useState(false);
    const [selectedTenantKey, setSelectedTenantKey] = useState(null);
    const [autoOpenHandled, setAutoOpenHandled] = useState(false);

    // Add / Edit Tenant Modal State
    const [showEditTenantModal, setShowEditTenantModal] = useState(false);
    const [isAddTenantMode, setIsAddTenantMode] = useState(false);
    const [editingTenant, setEditingTenant] = useState(null);
    const [tenantFormData, setTenantFormData] = useState({
        tenant_name: '',
        tenant_id: '',
        acc_status: 'active',
        status_reason: ''
    });
    const [isSavingTenant, setIsSavingTenant] = useState(false);

    const handleOpenEditTenant = (tenant) => {
        setIsAddTenantMode(false);
        setEditingTenant(tenant);

        // Find the best non-blank tenant name from the tenant or its licenses
        let initialTenantName = tenant.tenant_name && tenant.tenant_name !== '-' && tenant.tenant_name.trim() !== ''
            ? tenant.tenant_name.trim()
            : '';
        if (!initialTenantName && tenant.licenses && tenant.licenses.length > 0) {
            const foundWithName = tenant.licenses.find(l => l.tenant_name && String(l.tenant_name).trim() && String(l.tenant_name).trim() !== '-');
            if (foundWithName) {
                initialTenantName = String(foundWithName.tenant_name).trim();
            }
        }

        setTenantFormData({
            tenant_name: initialTenantName,
            tenant_id: tenant.tenant_id,
            acc_status: tenant.acc_status || 'active',
            status_reason: tenant.status_reason && tenant.status_reason !== '-' ? tenant.status_reason : ''
        });
        setShowEditTenantModal(true);
    };

    const handleOpenAddTenant = () => {
        handleOpenAddLicense();
    };

    const handleSaveTenant = async (e) => {
        e.preventDefault();
        setIsSavingTenant(true);

        const targetSetupId = localSetupData?.id || setupData?.id;

        try {
            // ── Update all existing licenses for this tenant STRICTLY under THIS SETUP ONLY ──
            if (!editingTenant) { setIsSavingTenant(false); return; }

            let licensesToUpdate = (licenses || []).filter(lic => {
                const sameSetup = !targetSetupId || Number(lic.setup_id) === Number(targetSetupId);
                const sameTenant = editingTenant.tenant_id !== null && editingTenant.tenant_id !== undefined && editingTenant.tenant_id !== ''
                    ? (String(lic.tenant_id ?? '').trim() === String(editingTenant.tenant_id ?? '').trim() ||
                       Number(lic.tenant_id) === Number(editingTenant.tenant_id))
                    : (editingTenant.licenses || []).some(el => el.id === lic.id);
                return sameSetup && sameTenant;
            });

            if (licensesToUpdate.length === 0 && editingTenant.licenses && editingTenant.licenses.length > 0) {
                licensesToUpdate = editingTenant.licenses.filter(l => !targetSetupId || Number(l.setup_id) === Number(targetSetupId));
            }

            // Refresh licenses for THIS SETUP ONLY from backend if needed
            if (targetSetupId) {
                try {
                    const setupRes = await axios.get(`${backendUrl}/api/v360-license-info`, {
                        params: { setup_id: targetSetupId },
                        headers: getHeaders()
                    });
                    const setupFound = Array.isArray(setupRes.data?.data)
                        ? setupRes.data.data
                        : (Array.isArray(setupRes.data) ? setupRes.data : []);
                    const matchedInSetup = setupFound.filter(l => {
                        const sameSetup = Number(l.setup_id) === Number(targetSetupId);
                        const sameTenant = editingTenant.tenant_id !== null && editingTenant.tenant_id !== undefined && editingTenant.tenant_id !== ''
                            ? (String(l.tenant_id ?? '').trim() === String(editingTenant.tenant_id ?? '').trim() ||
                               Number(l.tenant_id) === Number(editingTenant.tenant_id))
                            : (editingTenant.licenses || []).some(el => el.id === l.id);
                        return sameSetup && sameTenant;
                    });
                    const existingIds = new Set(licensesToUpdate.map(l => l.id));
                    matchedInSetup.forEach(l => { if (!existingIds.has(l.id)) licensesToUpdate.push(l); });
                } catch (fetchErr) {
                    console.error('Error fetching setup licenses:', fetchErr);
                }
            }

            if (licensesToUpdate.length === 0) {
                Toast.fire({ icon: 'warning', title: 'No licenses found for this tenant in this setup' });
                setShowEditTenantModal(false);
                setIsSavingTenant(false);
                return;
            }

            // Patch all licenses under this tenant (STRICTLY for this setup only)
            await Promise.all(
                licensesToUpdate.map(lic =>
                    axios.patch(
                        `${backendUrl}/api/v360-license-info/${lic.id}`,
                        {
                            acc_status: tenantFormData.acc_status,
                            status_reason: tenantFormData.status_reason,
                            tenant_name: tenantFormData.tenant_name
                        },
                        { headers: getHeaders() }
                    )
                )
            );

                Toast.fire({ icon: 'success', title: 'Tenant and all licenses updated successfully' });

            setShowEditTenantModal(false);
            setEditingTenant(null);
            setIsAddTenantMode(false);
            await fetchLicenses();
            if (onSetupUpdated) onSetupUpdated();
        } catch (error) {
            console.error('Error saving tenant:', error);
            Toast.fire({ icon: 'error', title: 'Failed to save tenant' });
        } finally {
            setIsSavingTenant(false);
        }
    };

    useEffect(() => {
        if (show && setupData?.id) {
            setActiveTab(defaultTab);
            setIsEditingKey(false);
            setIsEditingClientName(false);
            setIsEditingClientId(false);
            setLocalSetupData(setupData);
            setSearchQuery('');
            setAccStatusTab('all');
            setCurrentPage(0);
            setSelectedTenantKey(null);
            setShowTenantDetails(false);
            setAutoOpenHandled(false);
            if (activeExpiryFilter !== undefined) {
                setExpiryFilter(activeExpiryFilter);
            }
            fetchLicenses(setupData.id);
        } else if (!show) {
            setLicenses([]);
        }
    }, [show, setupData?.id, defaultTab, activeExpiryFilter]);

    const fetchLicenses = async (overrideSetupId) => {
        const targetSetupId = overrideSetupId || setupData?.id || localSetupData?.id;
        if (!targetSetupId) return;

        setIsLoadingLicenses(true);
        try {
            const params = { setup_id: targetSetupId };
            const response = await axios.get(`${backendUrl}/api/v360-license-info`, { params, headers: getHeaders() });
            const resData = response.data;
            if (resData && resData.data) {
                setLicenses(Array.isArray(resData.data) ? resData.data : []);
            } else if (Array.isArray(resData)) {
                setLicenses(resData);
            } else {
                setLicenses([]);
            }
        } catch (error) {
            console.error('Error fetching license info', error);
            Toast.fire({ icon: 'error', title: 'Failed to load license infos' });
        } finally {
            setIsLoadingLicenses(false);
        }
    };

    // Auto open license if requested
    useEffect(() => {
        if (!autoOpenHandled && autoOpenLicenseId && licenses.length > 0) {
            const matched = licenses.find(l => l.id === autoOpenLicenseId || l.license_id === autoOpenLicenseId);
            if (matched) {
                const tKey = (matched.tenant_id !== null && matched.tenant_id !== undefined && String(matched.tenant_id).trim() !== '')
                    ? String(matched.tenant_id).trim()
                    : '__unassigned__';
                setSelectedTenantKey(tKey);
                setShowTenantDetails(true);
                setAutoOpenHandled(true);
            }
        }
    }, [autoOpenLicenseId, licenses, autoOpenHandled]);

    // Group licenses by tenant_id
    const groupedTenants = useMemo(() => {
        const currentSetupId = localSetupData?.id || setupData?.id;
        const relevantLicenses = currentSetupId
            ? licenses.filter(info => Number(info.setup_id) === Number(currentSetupId))
            : licenses;

        const tenantMap = new Map();

        relevantLicenses.forEach((lic) => {
            const rawTenantId = lic.tenant_id;
            // Group strictly using tenant_id, not tenant name or remark
            const key = (rawTenantId !== null && rawTenantId !== undefined && String(rawTenantId).trim() !== '')
                ? String(rawTenantId).trim()
                : '__unassigned__';

            const licTenantName = lic.tenant_name && String(lic.tenant_name).trim() && String(lic.tenant_name).trim() !== '-'
                ? String(lic.tenant_name).trim()
                : '';

            if (!tenantMap.has(key)) {
                tenantMap.set(key, {
                    tenant_id_key: key,
                    tenant_id: key === '__unassigned__' ? null : rawTenantId,
                    tenant_name: licTenantName,
                    licenses: []
                });
            }

            const group = tenantMap.get(key);
            group.licenses.push(lic);

            if ((!group.tenant_name || group.tenant_name === '-') && licTenantName) {
                group.tenant_name = licTenantName;
            }
        });

        const tenantGroups = [];

        tenantMap.forEach((group) => {
            let minStartDate = null;
            let maxEndDate = null;

            let expiredCount = 0;
            let extendedCount = 0;
            let activeCount = 0;

            group.licenses.forEach((lic) => {
                if (lic.start_date) {
                    const sDate = parseValidDate(lic.start_date);
                    if (sDate) {
                        if (!minStartDate || sDate < minStartDate) {
                            minStartDate = sDate;
                        }
                    }
                }

                const endStr = lic.extended_validity || lic.validity;
                if (endStr) {
                    const eDate = parseValidDate(endStr);
                    if (eDate) {
                        if (!maxEndDate || eDate > maxEndDate) {
                            maxEndDate = eDate;
                        }
                    }
                }

                const st = getLicenseStatus(lic);
                if (st === 'Expired') expiredCount++;
                else if (st === 'Extended') extendedCount++;
                else activeCount++;
            });

            // Extract acc_status and status_reason from tenant's licenses
            const licWithAccStatus = group.licenses.find(l => l.acc_status) || group.licenses[0] || {};
            const acc_status = licWithAccStatus.acc_status
                ? String(licWithAccStatus.acc_status).toLowerCase()
                : 'active';
            const status_reason = licWithAccStatus.status_reason || '';

            // Determine aggregated tenant status accurately
            let statusLabel = 'Active';
            let statusBadgeClass = 'bg-success';
            let rowColorClass = '';

            if (expiredCount === group.licenses.length && group.licenses.length > 0) {
                statusLabel = 'Expired';
                statusBadgeClass = 'bg-danger';
                rowColorClass = 'text-danger fw-bold';
            } else if (expiredCount > 0) {
                statusLabel = `Partially Expired (${expiredCount}/${group.licenses.length})`;
                statusBadgeClass = 'bg-warning text-dark';
                rowColorClass = 'text-warning fw-bold';
            } else if (extendedCount > 0) {
                statusLabel = 'Extended';
                statusBadgeClass = 'bg-soft-success text-success border border-success';
            } else {
                statusLabel = 'Active';
                statusBadgeClass = 'bg-success';
            }

            // Find the best non-empty tenant name from group or licenses
            let finalTenantName = group.tenant_name && group.tenant_name !== '-' && group.tenant_name.trim() !== ''
                ? group.tenant_name.trim()
                : '';
            if (!finalTenantName) {
                const licWithName = group.licenses.find(l => l.tenant_name && String(l.tenant_name).trim() && String(l.tenant_name).trim() !== '-');
                if (licWithName) {
                    finalTenantName = String(licWithName.tenant_name).trim();
                }
            }

            tenantGroups.push({
                tenant_id_key: group.tenant_id_key,
                tenant_id: group.tenant_id,
                tenant_name: finalTenantName || null,
                acc_status,
                status_reason,
                minStartDate,
                maxEndDate,
                statusLabel,
                statusBadgeClass,
                rowColorClass,
                licenses: group.licenses,
                totalLicenses: group.licenses.length
            });
        });

        // Sort by tenant_id in ascending order (numbers first, unassigned/null at the end)
        tenantGroups.sort((a, b) => {
            if (a.tenant_id === null || a.tenant_id === undefined) return 1;
            if (b.tenant_id === null || b.tenant_id === undefined) return -1;
            const numA = Number(a.tenant_id);
            const numB = Number(b.tenant_id);
            if (!isNaN(numA) && !isNaN(numB)) {
                return numA - numB;
            }
            return String(a.tenant_id).localeCompare(String(b.tenant_id), undefined, { numeric: true });
        });

        return tenantGroups;
    }, [licenses, localSetupData?.id, setupData?.id]);

    // Currently selected tenant data for TenantInfoDetails
    const selectedTenantData = useMemo(() => {
        if (!selectedTenantKey) return null;
        return groupedTenants.find(t => t.tenant_id_key === selectedTenantKey) || null;
    }, [groupedTenants, selectedTenantKey]);

    // Tenant account status counts
    const tenantStatusCounts = useMemo(() => {
        let active = 0;
        let deactivated = 0;
        let suspended = 0;
        groupedTenants.forEach((t) => {
            const st = (t.acc_status || 'active').toLowerCase();
            if (st === 'deactivated') deactivated++;
            else if (st === 'suspended') suspended++;
            else active++;
        });
        return {
            all: groupedTenants.length,
            active,
            deactivated,
            suspended
        };
    }, [groupedTenants]);

    // Search, Status Tab, and Expiry Filter on grouped tenants
    const filteredTenants = useMemo(() => {
        let list = groupedTenants;

        // Filter by Account Status tab
        if (accStatusTab !== 'all') {
            list = list.filter((t) => (t.acc_status || 'active').toLowerCase() === accStatusTab);
        }

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            list = list.filter((t) => {
                const nameMatch = t.tenant_name && t.tenant_name.toLowerCase().includes(q);
                const idMatch = t.tenant_id !== null && String(t.tenant_id).toLowerCase().includes(q);
                const statusMatch = t.acc_status && t.acc_status.toLowerCase().includes(q);
                const reasonMatch = t.status_reason && t.status_reason.toLowerCase().includes(q);
                const licMatch = t.licenses.some(l => l.remark && l.remark.toLowerCase().includes(q));
                return nameMatch || idMatch || statusMatch || reasonMatch || licMatch;
            });
        }

        if (expiryFilter) {
            list = list.filter((t) => {
                return t.licenses.some((lic) => {
                    const valStr = lic.extended_validity || lic.validity;
                    if (!valStr) return expiryFilter === '';
                    const vDate = parseValidDate(valStr);
                    if (!vDate) return false;
                    vDate.setHours(0, 0, 0, 0);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const diffTime = vDate - today;
                    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

                    if (expiryFilter === 'expired') return diffDays < 0;
                    if (expiryFilter === 'expired_7_days') return diffDays < 0 && diffDays >= -7;
                    if (expiryFilter === '1_day') return diffDays === 1;
                    if (expiryFilter === '7_days') return diffDays <= 7 && diffDays >= 0;
                    if (expiryFilter === '30_days') return diffDays <= 30 && diffDays >= 0;
                    return true;
                });
            });
        }

        return list;
    }, [groupedTenants, accStatusTab, searchQuery, expiryFilter]);

    // Pagination
    const ITEMS_PER_PAGE = 10;
    const pageCount = Math.ceil(filteredTenants.length / ITEMS_PER_PAGE) || 1;
    const paginatedTenants = useMemo(() => {
        const start = currentPage * ITEMS_PER_PAGE;
        return filteredTenants.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredTenants, currentPage]);

    const handleOpenTenantDetails = (tenant) => {
        setSelectedTenantKey(tenant.tenant_id_key);
        setShowTenantDetails(true);
    };

    // Opens TenantInfoDetails with no tenantData to directly add a license
    const [showDirectAddLicense, setShowDirectAddLicense] = useState(false);
    const handleOpenAddLicense = () => {
        setShowDirectAddLicense(true);
    };

    const handleSaveKey = async () => {
        try {
            await axios.patch(`${backendUrl}/api/v360-setup-info/${localSetupData.id}`, { key: editedKey }, { headers: getHeaders() });
            setLocalSetupData(prev => ({ ...prev, key: editedKey }));
            setIsEditingKey(false);
            Toast.fire({ icon: 'success', title: 'Key updated successfully' });
            if (onSetupUpdated) onSetupUpdated();
        } catch (error) {
            console.error('Error updating key', error);
            Toast.fire({ icon: 'error', title: 'Failed to update key' });
        }
    };

    const handleSaveClientName = async () => {
        try {
            await axios.patch(`${backendUrl}/api/v360-setup-info/${localSetupData.id}`, { client_name: editedClientName }, { headers: getHeaders() });
            setLocalSetupData(prev => ({ ...prev, client_name: editedClientName }));
            setIsEditingClientName(false);
            Toast.fire({ icon: 'success', title: 'Client Name updated successfully' });
            if (onSetupUpdated) onSetupUpdated();
        } catch (error) {
            console.error('Error updating client name', error);
            Toast.fire({ icon: 'error', title: 'Failed to update client name' });
        }
    };

    const handleSaveClientId = async () => {
        try {
            await axios.patch(`${backendUrl}/api/v360-setup-info/${localSetupData.id}`, { client_id: editedClientId }, { headers: getHeaders() });
            setLocalSetupData(prev => ({ ...prev, client_id: editedClientId }));
            setIsEditingClientId(false);
            Toast.fire({ icon: 'success', title: 'Client ID updated successfully' });
            if (onSetupUpdated) onSetupUpdated();
        } catch (error) {
            console.error('Error updating client ID', error);
            Toast.fire({ icon: 'error', title: 'Failed to update client ID' });
        }
    };

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    return (
        <>
            <Offcanvas
                show={show}
                onHide={onHide}
                placement="end"
                style={{
                    width: '1180px',
                    zIndex: 1045,
                    visibility: (showTenantDetails || showEditTenantModal) ? 'hidden' : 'visible'
                }}
                className="Setup-offcanvas"
                unmountOnExit={false}
            >
                {/* Offcanvas Header */}
                <Offcanvas.Header className="border-bottom bg-white py-3">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="d-flex align-items-center">
                            <div className="bg-primary rounded-circle p-2 me-3">
                                <FaCogs className="text-white" size={18} />
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bold text-dark">{localSetupData?.client_name || 'Setup Details'}</h5>
                                <small className="text-muted">Setup ID: {localSetupData?.id}</small>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                    onEditSetup(setupData);
                                }}
                            >
                                <BiPencil className="me-1" />
                                Edit Setup
                            </Button>

                            <Button
                                variant="light"
                                size="sm"
                                onClick={onHide}
                                className="border-0"
                            >
                                <BsX size={20} />
                            </Button>
                        </div>
                    </div>
                </Offcanvas.Header>

                {/* Offcanvas Body */}
                <Offcanvas.Body className="p-0 bg-light">
                    {/* Summary Card */}
                    <div className="p-4 border-bottom bg-white">
                        <div className="row g-4">
                            <div className="col-md-4">
                                <div className="d-flex align-items-center">
                                    <div className="bg-light rounded-circle p-2 shadow-sm me-3">
                                        <FaIdCard className="text-primary" size={16} />
                                    </div>
                                    <div>
                                        <label className="text-muted small mb-1 d-block">Key</label>
                                        <h6 className="mb-0 fw-semibold text-dark text-break">
                                            {localSetupData?.key || '-'}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="d-flex align-items-center">
                                    <div className="bg-light rounded-circle p-2 shadow-sm me-3">
                                        <FaServer className="text-primary" size={16} />
                                    </div>
                                    <div>
                                        <label className="text-muted small mb-1 d-block">Setup Mode</label>
                                        <h6 className="mb-0 fw-semibold text-dark">
                                            {setupData?.setup_mode || '-'}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="d-flex align-items-center">
                                    <div className="bg-light rounded-circle p-2 shadow-sm me-3">
                                        <FaNetworkWired className="text-primary" size={16} />
                                    </div>
                                    <div>
                                        <label className="text-muted small mb-1 d-block">IP Check</label>
                                        <h6 className="mb-0 fw-semibold text-dark">
                                            {setupData?.ip_check ? 'Enabled' : 'Disabled'}
                                        </h6>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="bg-white">
                        <ul className="nav nav-tabs justify-content-start px-4 pt-2 font-size-13" role="tablist" style={{ borderBottom: '2px solid #eff2f7' }}>
                            <li className="nav-item" role="presentation">
                                <a
                                    className={`nav-link d-flex align-items-center gap-2 fw-medium pb-3 px-3 border-0 ${activeTab === 'licenseList' ? 'active text-primary' : 'text-muted'}`}
                                    onClick={() => handleTabClick('licenseList')}
                                    role="tab"
                                    style={{ cursor: 'pointer', borderBottom: activeTab === 'licenseList' ? '2px solid #556ee6' : '2px solid transparent', marginBottom: '-2px', background: 'transparent', transition: 'all 0.2s ease' }}
                                >
                                    <i className="bx bx-buildings font-size-16"></i>
                                    Tenant Info
                                    <span className={`badge rounded-pill ms-1 font-size-11 ${activeTab === 'licenseList' ? 'bg-primary text-white' : 'bg-light text-muted border'}`}>
                                        {groupedTenants.length}
                                    </span>
                                </a>
                            </li>
                            <li className="nav-item" role="presentation">
                                <a
                                    className={`nav-link d-flex align-items-center gap-2 fw-medium pb-3 px-3 border-0 ${activeTab === 'details' ? 'active text-primary' : 'text-muted'}`}
                                    onClick={() => handleTabClick('details')}
                                    role="tab"
                                    style={{ cursor: 'pointer', borderBottom: activeTab === 'details' ? '2px solid #556ee6' : '2px solid transparent', marginBottom: '-2px', background: 'transparent', transition: 'all 0.2s ease' }}
                                >
                                    <i className="bx bx-cog font-size-16"></i>
                                    Setup Details
                                </a>
                            </li>
                        </ul>

                        {/* Tab Content */}
                        <div className="tab-content">
                            {activeTab === 'licenseList' && (
                                <div className="tab-pane active" role="tabpanel">
                                    <div className="p-3">
                                        {/* Top Controls: Search, Expiry Filter & Add License */}
                                        <div className="d-flex justify-content-end align-items-center gap-2 mb-3 mt-2">
                                            <div className="search-box">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search tenant or remark..."
                                                        value={searchQuery}
                                                        onChange={(e) => {
                                                            setSearchQuery(e.target.value);
                                                            setCurrentPage(0);
                                                        }}
                                                    />
                                                    <i className="bx bx-search-alt search-icon"></i>
                                                </div>
                                            </div>

                                            <Dropdown>
                                                <Dropdown.Toggle as={CustomToggle} />
                                                <Dropdown.Menu className="shadow-lg border-0 rounded-3">
                                                    <Dropdown.Header className="fw-bold text-uppercase text-muted" style={{ fontSize: '10px', letterSpacing: '1px' }}>Filter by Expiry</Dropdown.Header>
                                                    <Dropdown.Item active={expiryFilter === ''} onClick={() => { setExpiryFilter(''); setCurrentPage(0); }}>All Licenses</Dropdown.Item>
                                                    <Dropdown.Divider />
                                                    <Dropdown.Item active={expiryFilter === 'expired'} onClick={() => { setExpiryFilter('expired'); setCurrentPage(0); }}>
                                                        <span className="text-danger fw-bold"><i className="mdi mdi-alert-circle-outline me-1"></i>Expired</span>
                                                    </Dropdown.Item>
                                                    <Dropdown.Item active={expiryFilter === 'expired_7_days'} onClick={() => { setExpiryFilter('expired_7_days'); setCurrentPage(0); }}>
                                                        <span className="text-danger"><i className="mdi mdi-clock-alert-outline me-1"></i>Expired in 7 days</span>
                                                    </Dropdown.Item>
                                                    <Dropdown.Divider />
                                                    <Dropdown.Item active={expiryFilter === '1_day'} onClick={() => { setExpiryFilter('1_day'); setCurrentPage(0); }}>
                                                        <span className="text-warning"><i className="mdi mdi-timer-sand me-1"></i>Expiring in 1 Day</span>
                                                    </Dropdown.Item>
                                                    <Dropdown.Item active={expiryFilter === '7_days'} onClick={() => { setExpiryFilter('7_days'); setCurrentPage(0); }}>
                                                        <span className="text-warning"><i className="mdi mdi-calendar-alert me-1"></i>Expiring in 7 Days</span>
                                                    </Dropdown.Item>
                                                    <Dropdown.Item active={expiryFilter === '30_days'} onClick={() => { setExpiryFilter('30_days'); setCurrentPage(0); }}>
                                                        <span className="text-info"><i className="mdi mdi-calendar-clock me-1"></i>Expiring in 30 Days</span>
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>

                                            <button
                                                type="button"
                                                className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                onClick={handleOpenAddLicense}
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Add License"
                                            >
                                                <i className="mdi mdi-plus"></i>
                                            </button>
                                        </div>

                                        {/* Standard Sub-Tabs: All, Active, Deactivated, Suspended */}
                                        <ul className="nav nav-tabs nav-tabs-custom mb-3 font-size-13" role="tablist" style={{ borderBottom: '1px solid #eff2f7' }}>
                                            <li className="nav-item">
                                                <button
                                                    type="button"
                                                    className={`nav-link border-0 bg-transparent py-2 px-3 fw-medium ${accStatusTab === 'all' ? 'active' : 'text-muted'}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => { setAccStatusTab('all'); setCurrentPage(0); }}
                                                >
                                                    All
                                                    <span className={`badge rounded-pill ms-2 ${accStatusTab === 'all' ? 'bg-primary text-white' : 'bg-light text-muted border'}`}>
                                                        {tenantStatusCounts.all}
                                                    </span>
                                                </button>
                                            </li>
                                            <li className="nav-item">
                                                <button
                                                    type="button"
                                                    className={`nav-link border-0 bg-transparent py-2 px-3 fw-medium ${accStatusTab === 'active' ? 'active' : 'text-muted'}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => { setAccStatusTab('active'); setCurrentPage(0); }}
                                                >
                                                    Active
                                                    <span className={`badge rounded-pill ms-2 ${accStatusTab === 'active' ? 'bg-success text-white' : 'bg-soft-success text-success border border-success border-opacity-25'}`}>
                                                        {tenantStatusCounts.active}
                                                    </span>
                                                </button>
                                            </li>
                                            <li className="nav-item">
                                                <button
                                                    type="button"
                                                    className={`nav-link border-0 bg-transparent py-2 px-3 fw-medium ${accStatusTab === 'deactivated' ? 'active' : 'text-muted'}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => { setAccStatusTab('deactivated'); setCurrentPage(0); }}
                                                >
                                                    Deactivated
                                                    <span className={`badge rounded-pill ms-2 ${accStatusTab === 'deactivated' ? 'bg-danger text-white' : 'bg-soft-danger text-danger border border-danger border-opacity-25'}`}>
                                                        {tenantStatusCounts.deactivated}
                                                    </span>
                                                </button>
                                            </li>
                                            <li className="nav-item">
                                                <button
                                                    type="button"
                                                    className={`nav-link border-0 bg-transparent py-2 px-3 fw-medium ${accStatusTab === 'suspended' ? 'active' : 'text-muted'}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => { setAccStatusTab('suspended'); setCurrentPage(0); }}
                                                >
                                                    Suspended
                                                    <span className={`badge rounded-pill ms-2 ${accStatusTab === 'suspended' ? 'bg-warning text-dark' : 'bg-soft-warning text-warning border border-warning border-opacity-25'}`}>
                                                        {tenantStatusCounts.suspended}
                                                    </span>
                                                </button>
                                            </li>
                                        </ul>

                                        {/* Tenant-wise Grouped List (Main View) */}
                                        <div className="contacts-table-modern">
                                            <div className="table-responsive" style={{ overflow: 'visible', minHeight: '200px', paddingBottom: '100px' }}>
                                                <table className="table table-modern align-middle">
                                                    <thead>
                                                        <tr>
                                                            <th className="col" style={{ width: '60px' }}>#</th>
                                                            <th className="col">
                                                                <div className="d-flex justify-content-center">
                                                                    <div style={{ width: '150px', textAlign: 'left' }}>
                                                                        Tenant Name
                                                                    </div>
                                                                </div>
                                                            </th>
                                                            <th className="col">Tenant ID</th>
                                                            <th className="col">Status</th>
                                                            <th className="col">Status Reason</th>
                                                            <th className="col" style={{ width: '80px' }}>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {isLoadingLicenses ? (
                                                            <tr>
                                                                <td colSpan="6" className="text-center py-4">
                                                                    <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                                                                    <span className="text-muted font-size-13">Loading tenant licenses...</span>
                                                                </td>
                                                            </tr>
                                                        ) : paginatedTenants.length > 0 ? (
                                                            paginatedTenants.map((tenant, idx) => {
                                                                const st = (tenant.acc_status || 'active').toLowerCase();
                                                                const statusBadge = st === 'deactivated'
                                                                    ? 'bg-soft-danger text-danger border border-danger border-opacity-25'
                                                                    : st === 'suspended'
                                                                        ? 'bg-soft-warning text-warning border border-warning border-opacity-25'
                                                                        : 'bg-soft-success text-success border border-success border-opacity-25';

                                                                return (
                                                                    <tr key={tenant.tenant_id_key}>
                                                                        <td>
                                                                            {(currentPage * ITEMS_PER_PAGE) + idx + 1}
                                                                        </td>
                                                                        <td
                                                                            className="text-black fw-semibold"
                                                                            style={{ cursor: 'pointer' }}
                                                                            onClick={() => handleOpenTenantDetails(tenant)}
                                                                            title="Click to view tenant details and licenses"
                                                                        >
                                                                            <div className="d-flex justify-content-center">
                                                                                <div style={{ width: '150px', textAlign: 'left' }}>
                                                                                    {tenant.tenant_name ? (
                                                                                        <span className="text-break">{tenant.tenant_name}</span>
                                                                                    ) : (
                                                                                        <span className="text-muted fw-semibold">null</span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td>
                                                                            {tenant.tenant_id !== null ? tenant.tenant_id : 'Unassigned'}
                                                                        </td>
                                                                        <td>
                                                                            <span className={`badge rounded-pill px-2.5 py-1 font-size-11 ${statusBadge}`}>
                                                                                {tenant.acc_status || 'active'}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-muted">
                                                                            {tenant.status_reason || '-'}
                                                                        </td>
                                                                        <td>
                                                                            <div className="dropdown">
                                                                                <i
                                                                                    className="mdi mdi-dots-horizontal font-size-18"
                                                                                    data-bs-toggle="dropdown"
                                                                                    aria-expanded="false"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                ></i>
                                                                                <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                                    <li>
                                                                                        <button
                                                                                            className="dropdown-item text-primary"
                                                                                            onClick={() => handleOpenTenantDetails(tenant)}
                                                                                        >
                                                                                            View Details
                                                                                        </button>
                                                                                    </li>
                                                                                    <li>
                                                                                        <button
                                                                                            className="dropdown-item"
                                                                                            onClick={() => handleOpenEditTenant(tenant)}
                                                                                        >
                                                                                            Edit
                                                                                        </button>
                                                                                    </li>
                                                                                </ul>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="6" className="text-center py-5">
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <div className="mb-3">
                                                                            <FaBuilding className="text-muted" size={36} style={{ opacity: 0.35 }} />
                                                                        </div>
                                                                        <p className="text-muted mb-3">
                                                                            {accStatusTab !== 'all'
                                                                                ? `No ${accStatusTab} tenants found.`
                                                                                : searchQuery
                                                                                    ? 'No matching tenants found.'
                                                                                    : 'No licenses found for this setup.'}
                                                                        </p>
                                                                        <Button
                                                                            variant="primary"
                                                                            size="sm"
                                                                            onClick={handleOpenAddLicense}
                                                                            className="d-flex align-items-center"
                                                                        >
                                                                            <i className="mdi mdi-plus me-1 font-size-16"></i> Add License
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {/* Pagination for tenant-grouped list */}
                                            {pageCount > 1 && (
                                                <div className="d-flex justify-content-end mt-3">
                                                    <ReactPaginate
                                                        previousLabel={"<"}
                                                        nextLabel={">"}
                                                        breakLabel={"..."}
                                                        pageCount={pageCount}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={3}
                                                        onPageChange={(data) => setCurrentPage(data.selected)}
                                                        forcePage={currentPage}
                                                        containerClassName={"pagination pagination-rounded"}
                                                        pageClassName={"page-item"}
                                                        pageLinkClassName={"page-link"}
                                                        previousClassName={"page-item"}
                                                        previousLinkClassName={"page-link"}
                                                        nextClassName={"page-item"}
                                                        nextLinkClassName={"page-link"}
                                                        breakClassName={"page-item"}
                                                        breakLinkClassName={"page-link"}
                                                        activeClassName={"active"}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'details' && (
                                <div className="tab-pane active" role="tabpanel">
                                    <div className="p-4 bg-light">
                                        <div className="card shadow-sm border-0 mb-4">
                                            <div className="card-header bg-white border-0 py-3">
                                                <div className="d-flex align-items-center">
                                                    <FaCogs className="text-primary" />
                                                    <h5 className="mb-0 ms-2 fw-semibold text-dark">Setup Configuration</h5>
                                                </div>
                                            </div>
                                            <div className="card-body p-0">
                                                <div className="row g-0">
                                                    <div className="col-12">
                                                        <div className="p-3 border-bottom instance-detail-item h-100">
                                                            <div className="d-flex align-items-start">
                                                                <div className="text-muted me-3 mt-1"><FaIdCard size={14} /></div>
                                                                <div className="flex-grow-1">
                                                                    <label className="text-muted small mb-1 d-block fw-medium">Key</label>
                                                                    <div className="editable-container d-flex align-items-center">
                                                                        {isEditingKey ? (
                                                                            <div className="d-flex align-items-center w-100">
                                                                                <textarea
                                                                                    value={editedKey}
                                                                                    onChange={(e) => setEditedKey(e.target.value)}
                                                                                    autoFocus
                                                                                    rows={2}
                                                                                    style={{
                                                                                        border: 'none',
                                                                                        borderBottom: '1px solid #2F7EF7',
                                                                                        background: 'transparent',
                                                                                        outline: 'none',
                                                                                        padding: '0 0 2px 0',
                                                                                        margin: 0,
                                                                                        width: '100%',
                                                                                        minWidth: '200px',
                                                                                        fontSize: 'inherit',
                                                                                        fontWeight: '600',
                                                                                        color: '#343a40',
                                                                                        resize: 'none',
                                                                                        overflow: 'hidden'
                                                                                    }}
                                                                                />
                                                                                <div className="d-flex flex-column ms-2">
                                                                                    <BsCheck className="text-success mb-1" onClick={handleSaveKey} style={{ cursor: "pointer" }} size={22} />
                                                                                    <BsX className="text-danger" onClick={() => setIsEditingKey(false)} style={{ cursor: "pointer" }} size={22} />
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="hover1 w-100" onClick={() => { setEditedKey(localSetupData?.key || ''); setIsEditingKey(true); }}>
                                                                                <div className="text-dark fw-semibold text-break hover-field1 d-inline-block w-100" style={{ paddingLeft: 0 }}>
                                                                                    {localSetupData?.key || '-'}
                                                                                </div>
                                                                                <BsPencil className="text-primary ms-2 pencil-icon1 flex-shrink-0" style={{ cursor: 'pointer' }} title="Edit Key" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="p-3 border-bottom border-end instance-detail-item h-100">
                                                            <div className="d-flex align-items-start">
                                                                <div className="text-muted me-3 mt-1"><FaServer size={14} /></div>
                                                                <div className="flex-grow-1">
                                                                    <label className="text-muted small mb-1 d-block fw-medium">Client Name</label>
                                                                    <div className="editable-container d-flex align-items-center">
                                                                        {isEditingClientName ? (
                                                                            <div className="d-flex align-items-center w-100">
                                                                                <textarea
                                                                                    value={editedClientName}
                                                                                    onChange={(e) => setEditedClientName(e.target.value)}
                                                                                    autoFocus
                                                                                    rows={1}
                                                                                    style={{
                                                                                        border: 'none',
                                                                                        borderBottom: '1px solid #2F7EF7',
                                                                                        background: 'transparent',
                                                                                        outline: 'none',
                                                                                        padding: '0 0 2px 0',
                                                                                        margin: 0,
                                                                                        width: '100%',
                                                                                        minWidth: '200px',
                                                                                        fontSize: 'inherit',
                                                                                        fontWeight: '600',
                                                                                        color: '#343a40',
                                                                                        resize: 'none',
                                                                                        overflow: 'hidden'
                                                                                    }}
                                                                                />
                                                                                <div className="d-flex align-items-center ms-2">
                                                                                    <BsCheck className="text-success mx-1" onClick={handleSaveClientName} style={{ cursor: "pointer" }} size={22} />
                                                                                    <BsX className="text-danger mx-1" onClick={() => setIsEditingClientName(false)} style={{ cursor: "pointer" }} size={22} />
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="hover1" onClick={() => { setEditedClientName(localSetupData?.client_name || ''); setIsEditingClientName(true); }}>
                                                                                <div className="text-dark fw-semibold text-break hover-field1 d-inline-block" style={{ paddingLeft: 0 }}>
                                                                                    {localSetupData?.client_name || '-'}
                                                                                </div>
                                                                                <BsPencil className="text-primary ms-2 pencil-icon1" style={{ cursor: 'pointer' }} title="Edit Client Name" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="p-3 border-bottom instance-detail-item h-100">
                                                            <div className="d-flex align-items-start">
                                                                <div className="text-muted me-3 mt-1"><FaServer size={14} /></div>
                                                                <div className="flex-grow-1">
                                                                    <label className="text-muted small mb-1 d-block fw-medium">Client ID</label>
                                                                    <div className="editable-container d-flex align-items-center">
                                                                        {isEditingClientId ? (
                                                                            <div className="d-flex align-items-center w-100">
                                                                                <textarea
                                                                                    value={editedClientId}
                                                                                    onChange={(e) => setEditedClientId(e.target.value)}
                                                                                    autoFocus
                                                                                    rows={1}
                                                                                    style={{
                                                                                        border: 'none',
                                                                                        borderBottom: '1px solid #2F7EF7',
                                                                                        background: 'transparent',
                                                                                        outline: 'none',
                                                                                        padding: '0 0 2px 0',
                                                                                        margin: 0,
                                                                                        width: '100%',
                                                                                        minWidth: '200px',
                                                                                        fontSize: 'inherit',
                                                                                        fontWeight: '600',
                                                                                        color: '#343a40',
                                                                                        resize: 'none',
                                                                                        overflow: 'hidden'
                                                                                    }}
                                                                                />
                                                                                <div className="d-flex align-items-center ms-2">
                                                                                    <BsCheck className="text-success mx-1" onClick={handleSaveClientId} style={{ cursor: "pointer" }} size={22} />
                                                                                    <BsX className="text-danger mx-1" onClick={() => setIsEditingClientId(false)} style={{ cursor: "pointer" }} size={22} />
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="hover1" onClick={() => { setEditedClientId(localSetupData?.client_id || ''); setIsEditingClientId(true); }}>
                                                                                <div className="text-dark fw-semibold text-break hover-field1 d-inline-block" style={{ paddingLeft: 0 }}>
                                                                                    {localSetupData?.client_id || '-'}
                                                                                </div>
                                                                                <BsPencil className="text-primary ms-2 pencil-icon1" style={{ cursor: 'pointer' }} title="Edit Client ID" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="p-3 border-bottom border-end instance-detail-item h-100">
                                                            <div className="d-flex align-items-start">
                                                                <div className="text-muted me-3 mt-1"><FaCogs size={14} /></div>
                                                                <div className="flex-grow-1">
                                                                    <label className="text-muted small mb-1 d-block fw-medium">Setup Mode</label>
                                                                    <div className="text-dark fw-semibold text-capitalize">{setupData?.setup_mode || '-'}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="p-3 border-bottom instance-detail-item h-100">
                                                            <div className="d-flex align-items-start">
                                                                <div className="text-muted me-3 mt-1"><FaNetworkWired size={14} /></div>
                                                                <div className="flex-grow-1">
                                                                    <label className="text-muted small mb-1 d-block fw-medium">IP Check</label>
                                                                    <div className="text-dark fw-semibold">
                                                                        <span className={`badge ${setupData?.ip_check ? 'bg-success' : 'bg-secondary'}`}>
                                                                            {setupData?.ip_check ? 'Enabled' : 'Disabled'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {setupData?.ip_check && (
                                                        <div className="col-12">
                                                            <div className="p-3 border-bottom instance-detail-item h-100">
                                                                <div className="d-flex align-items-start">
                                                                    <div className="text-muted me-3 mt-1"><FaNetworkWired size={14} /></div>
                                                                    <div className="flex-grow-1">
                                                                        <label className="text-muted small mb-1 d-block fw-medium">IP List</label>
                                                                        <div className="text-dark fw-semibold">{setupData?.ip_list ? setupData.ip_list.join(', ') : '-'}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="col-md-6">
                                                        <div className="p-3 border-bottom border-end instance-detail-item h-100">
                                                            <div className="d-flex align-items-start">
                                                                <div className="text-muted me-3 mt-1"><FaCogs size={14} /></div>
                                                                <div className="flex-grow-1">
                                                                    <label className="text-muted small mb-1 d-block fw-medium">Setup Type</label>
                                                                    <div className="text-dark fw-semibold text-capitalize">{setupData?.setup_type || '-'}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="p-3 border-end instance-detail-item h-100" style={{ borderBottom: 'none' }}>
                                                            <div className="d-flex align-items-start">
                                                                <div className="text-muted me-3 mt-1"><FaIdCard size={14} /></div>
                                                                <div className="flex-grow-1">
                                                                    <label className="text-muted small mb-1 d-block fw-medium">Max Tenants</label>
                                                                    <div className="text-dark fw-semibold">{setupData?.max_tenants || '0'}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="p-3 instance-detail-item h-100" style={{ borderBottom: 'none' }}>
                                                            <div className="d-flex align-items-start">
                                                                <div className="text-muted me-3 mt-1"><FaCogs size={14} /></div>
                                                                <div className="flex-grow-1">
                                                                    <label className="text-muted small mb-1 d-block fw-medium">Max Process</label>
                                                                    <div className="text-dark fw-semibold">{setupData?.max_process || '0'}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            {/* Tenant Details Component Offcanvas (from clicking a row) */}
            {selectedTenantData && (
                <TenantInfoDetails
                    show={showTenantDetails}
                    onHide={() => {
                        setShowTenantDetails(false);
                        setSelectedTenantKey(null);
                    }}
                    tenantData={selectedTenantData}
                    setupId={localSetupData?.id || setupData?.id}
                    onLicensesUpdated={fetchLicenses}
                />
            )}

            {/* Direct Add License — opened from empty state */}
            <TenantInfoDetails
                show={showDirectAddLicense}
                onHide={() => {
                    setShowDirectAddLicense(false);
                }}
                tenantData={null}
                setupId={localSetupData?.id || setupData?.id}
                onLicensesUpdated={fetchLicenses}
                autoOpenLicense={showDirectAddLicense}
            />

            {/* Add / Edit Tenant Offcanvas */}
            <Offcanvas
                show={showEditTenantModal}
                onHide={() => setShowEditTenantModal(false)}
                placement="end"
                style={{ width: '500px', zIndex: 1060 }}
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="fw-bold text-primary">
                        Edit Tenant
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <hr style={{ margin: 0 }} />
                <Offcanvas.Body>
                    <Form onSubmit={handleSaveTenant}>
                        <Form.Group className="mb-3">
                            <Form.Label className="form-label form-font">Tenant Name</Form.Label>
                            <Form.Control
                                type="text"
                                className="form-feild"
                                value={tenantFormData.tenant_name}
                                onChange={(e) => setTenantFormData({ ...tenantFormData, tenant_name: e.target.value })}
                                placeholder="Enter Tenant Name"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="form-label form-font">Tenant ID</Form.Label>
                            <Form.Control
                                type="text"
                                className="form-feild bg-light text-muted"
                                value={tenantFormData.tenant_id !== null && tenantFormData.tenant_id !== undefined ? tenantFormData.tenant_id : 'Unassigned'}
                                disabled
                                readOnly
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="form-label form-font">Account Status</Form.Label>
                            <Form.Select
                                className="form-feild"
                                value={tenantFormData.acc_status}
                                onChange={(e) => setTenantFormData({ ...tenantFormData, acc_status: e.target.value })}
                                required
                            >
                                <option value="active">Active</option>
                                <option value="deactivated">Deactivated</option>
                                <option value="suspended">Suspended</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="form-label form-font">Status Reason</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                className="form-feild"
                                value={tenantFormData.status_reason}
                                onChange={(e) => setTenantFormData({ ...tenantFormData, status_reason: e.target.value })}
                                placeholder="Enter status reason"
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-end mt-4 mb-4">
                            <Button
                                variant="secondary"
                                onClick={() => setShowEditTenantModal(false)}
                                className="me-2"
                                disabled={isSavingTenant}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isSavingTenant}
                            >
                                {isSavingTenant ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    </Form>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default SetupInfoDetails;
